<template>
  <div class="pi-kpi" :class="{ 'is-link': !!route }">
    <div class="pi-kpi__head">
      <router-link v-if="route" :to="route" class="pi-kpi__label pi-kpi__link">{{ label }}</router-link>
      <span v-else class="pi-kpi__label">{{ label }}</span>
      <pi-definition-tooltip :definition-key="definitionKeyResolved" :info-key="infoKey" :label="label" :version="definitionVersion"
                             :overrides="definitionOverrides" :align="tooltipAlign"/>
    </div>

    <div class="pi-kpi__value" :class="{ 'is-muted': !visible }">{{ valueText }}</div>

    <div class="pi-kpi__state" v-if="badge">
      <span class="pi-badge" :class="'pi-badge--' + tone" :title="description">{{ badgeText }}</span>
      <pi-definition-tooltip v-if="badgeInfo" class="pi-kpi__state-info" :info-key="badgeInfo" :label="badgeText" :align="tooltipAlign"/>
    </div>
    <p v-if="metric && normalized.stateReason" class="pi-kpi__reason">{{ normalized.stateReason }}</p>

    <p v-if="compare && deltaText" class="pi-kpi__delta">
      <span class="pi-kpi__delta-value">{{ deltaText }}</span>
      <span class="pi-kpi__prev">vs. {{ previousText }} no período anterior</span>
    </p>
    <p v-else-if="compare && visible" class="pi-kpi__delta pi-kpi__prev">Sem base de comparação</p>

    <p v-if="ratioText" class="pi-kpi__ratio">{{ ratioText }}</p>
    <slot/>

    <pi-sparkline v-if="sparkline && sparkline.length" class="pi-kpi__spark" :points="sparkline"
                  :unit="normalized.unit" :label="label"/>
  </div>
</template>

<script>
import piDefinitionTooltip from "./piDefinitionTooltip.vue";
import piSparkline from "./piSparkline.vue";
import { formatDelta, formatInteger, formatValue, isNumber } from "./piFormat.js";
import {
  deltaAllowed, deltaPctAllowed, metricText, needsBadge, normalizeMetric, showsValue,
  stateDescription, stateLabel, stateTone,
} from "./piState.js";
import { safeInternalRoute } from "./piNavigation.js";
import { glossaryEntry, stateInfoKey } from "./glossary/index.js";

export default {
  name: "pi-kpi-card",
  components: { piDefinitionTooltip, piSparkline },
  props: {
    label: { type: String, required: true },
    metric: { type: Object, default: null },
    sparkline: { type: Array, default: () => [] },
    to: { type: [String, Object], default: "" },
    definitionKey: { type: String, default: "" },
    infoKey: { type: String, default: "" },
    definitionVersion: { type: Number, default: null },
    definitionOverrides: { type: Array, default: () => [] },
    compare: { type: Boolean, default: true },
    tooltipAlign: { type: String, default: "left" },
  },
  computed: {
    normalized() {
      return normalizeMetric(this.metric);
    },
    route() {
      if (this.to && typeof this.to === "object") return safeInternalRoute(this.to.path) ? this.to : null;
      return safeInternalRoute(this.to);
    },
    definitionKeyResolved() {
      return this.definitionKey || this.normalized.definitionKey || "";
    },
    visible() {
      return showsValue(this.normalized);
    },
    valueText() {
      return metricText(this.normalized);
    },
    badge() {
      return needsBadge(this.normalized) && this.badgeText !== this.valueText;
    },
    badgeText() {
      return stateLabel(this.normalized.state);
    },
    tone() {
      return stateTone(this.normalized.state);
    },
    badgeInfo() {
      const key = stateInfoKey(this.normalized.state);
      return glossaryEntry(key) ? key : "";
    },
    description() {
      return stateDescription(this.normalized.state, this.normalized.stateReason);
    },
    deltaText() {
      const metric = this.normalized;
      if (!deltaAllowed(metric)) return "";
      return formatDelta(metric.deltaAbs, deltaPctAllowed(metric) ? metric.deltaPct : null, metric.unit);
    },
    previousText() {
      return formatValue(this.normalized.previous, this.normalized.unit);
    },
    ratioText() {
      const metric = this.normalized;
      if (!isNumber(metric.numerator) || !isNumber(metric.denominator)) return "";
      return formatInteger(metric.numerator) + " de " + formatInteger(metric.denominator);
    },
  },
};
</script>

<style src="./pi.css"></style>

<style scoped>
.pi-kpi {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 14px 16px;
  border-radius: 6px;
  background: #ffffff;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.06);
}

.pi-kpi.is-link:hover {
  box-shadow: 0 3px 8px 0 rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(27, 89, 248, 0.35);
}

.pi-kpi__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.pi-kpi__label {
  font-size: 12px;
  font-weight: 700;
  color: #4e5560;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.pi-kpi__link {
  color: #3a424e;
  text-decoration: none;
}

.pi-kpi__link::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: 6px;
}

.pi-kpi__link:hover,
.pi-kpi__link:focus {
  color: #1b59f8;
  text-decoration: underline;
}

.pi-kpi__link:focus {
  outline: none;
}

.pi-kpi__link:focus::after {
  box-shadow: 0 0 0 3px #1b59f8;
}

.pi-kpi__value {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  color: #1f2933;
}

.pi-kpi__value.is-muted {
  font-size: 18px;
  color: #6b7280;
}

.pi-kpi__reason,
.pi-kpi__ratio,
.pi-kpi__delta {
  margin: 0;
  font-size: 12px;
  color: #4e5560;
}

.pi-kpi__delta-value {
  font-weight: 700;
  color: #1f2933;
  margin-right: 4px;
}

.pi-kpi__prev {
  color: #6b7280;
}

.pi-kpi__spark {
  margin-top: 6px;
}

.pi-kpi__state-info {
  margin-left: 4px;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/components/pi/piKpiCard.vue